import { useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { getPageBySlug } from '@/api/wiki'
import type { WikiPage as WikiPageType } from '@/types/wiki'
import TableOfContents from '@/components/wiki/TableOfContents'
import PrevNextNav from '@/components/wiki/PrevNextNav'
import { transformWikiContentHtml } from '@/utils/transformWikiContentHtml'

export default function WikiPage() {
  const { slug } = useParams<{ slug: string }>()
  const [page, setPage] = useState<WikiPageType | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)

  const rawHtml = page?.content_html ?? ''
  const contentHtml = useMemo(
    () => transformWikiContentHtml(rawHtml),
    [rawHtml],
  )
  const isChangelogPage = rawHtml.includes('changelog-page')
  const isShowcasePage = rawHtml.includes('factory-production-page')
    || rawHtml.includes('dragon-rings-page')

  useEffect(() => {
    if (!slug) return
    setLoading(true)
    setError(false)
    getPageBySlug(slug)
      .then(setPage)
      .catch(() => setError(true))
      .finally(() => setLoading(false))
  }, [slug])

  if (loading) {
    return (
      <div className="panel p-8">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-calamity-panel-hover rounded-lg w-2/3" />
          <div className="h-4 bg-calamity-panel-hover rounded w-3/4" />
          <div className="h-4 bg-calamity-panel-hover rounded w-1/2" />
          <div className="h-4 bg-calamity-panel-hover rounded w-5/6" />
          <div className="h-4 bg-calamity-panel-hover rounded w-2/3" />
        </div>
      </div>
    )
  }

  if (error || !page) {
    return (
      <div className="panel p-8 text-center">
        <div className="text-5xl mb-4 opacity-50">&#128269;</div>
        <h1 className="text-2xl font-display text-calamity-text mb-2">Page Not Found</h1>
        <p className="text-calamity-text-dim">
          This page doesn't exist or hasn't been published yet.
        </p>
      </div>
    )
  }

  return (
    <div className="flex gap-4">
      <article className={`panel wiki-readable-panel p-6 sm:p-8 flex-1 min-w-0 animate-fade-up ${isChangelogPage ? 'wiki-changelog-readable-panel' : ''} ${isShowcasePage ? 'wiki-showcase-readable-panel' : ''}`}>
        {/* Cover image */}
        {page.cover_image_path && (
          <div className="-mx-6 -mt-6 sm:-mx-8 sm:-mt-8 mb-6 overflow-hidden rounded-t-2xl">
            <img
              src={page.cover_image_path}
              alt={page.title}
              className="w-full h-48 sm:h-64 object-cover object-center"
            />
          </div>
        )}

        <div className="wiki-article">
          {/* Header */}
          <header className="wiki-page-header mb-8">
            <div className="flex items-center gap-3 mb-2">
              {page.icon && <span className="text-4xl">{page.icon}</span>}
              <h1 className="text-3xl sm:text-4xl font-sans font-semibold tracking-tight text-calamity-text leading-tight">
                {page.title}
              </h1>
            </div>
            {page.meta_description && (
              <p className="text-base text-calamity-text-dim mt-2">{page.meta_description}</p>
            )}
            <div className="flex items-center gap-4 mt-4 text-xs text-calamity-text-muted">
              <span>Last updated: {new Date(page.updated_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
              {page.category_name && (
                <span className="px-2.5 py-1 rounded-full bg-calamity-accent/30 border border-calamity-accent/45 text-calamity-text text-[11px] font-semibold">
                  {page.category_name}
                </span>
              )}
            </div>
          </header>

          {/* Content */}
          <div
            className="wiki-content"
            dangerouslySetInnerHTML={{ __html: contentHtml }}
          />

          <PrevNextNav currentSlug={page.slug} />
        </div>
      </article>

      {/* Table of contents */}
      <TableOfContents contentHtml={page.content_html} />
    </div>
  )
}
